// How an identity is labelled in the UI (PRD-0031 slice 04).
//
// A manual identity may have no name at all. The label is composed here, never
// stored: the server only keeps `unnamed_seq`, the case-stable number, so the
// same person reads "Unknown 3" in English and "Neznámy 3" in Slovak, and the
// number survives a reload.

/** The naming parts of a person, as the API serves them. */
export interface IdentityNameParts {
  name?: string | null
  nickname?: string | null
  unnamedSeq?: number | null
}

/** Translator shape — `t` of react-i18next, narrowed to what is used here. */
type Translate = (key: string, options?: { n: number }) => string

const trimmed = (value: string | null | undefined): string => (value ?? '').trim()

/**
 * Name, else nickname, else the translated "Unknown {n}".
 *
 * Falls back to the plain unknown label when the person has no number either —
 * a legacy row without `unnamed_seq` still gets a readable label.
 */
export function identityDisplayName(parts: IdentityNameParts, t: Translate): string {
  const name = trimmed(parts.name)
  if (name) return name
  const nickname = trimmed(parts.nickname)
  if (nickname) return nickname
  if (typeof parts.unnamedSeq === 'number') {
    return t('identity.unnamed', { n: parts.unnamedSeq })
  }
  return t('identity.unnamedUnknown')
}

/** True when the label is the generated placeholder rather than a real name. */
export function isUnnamedIdentity(parts: IdentityNameParts): boolean {
  return !trimmed(parts.name) && !trimmed(parts.nickname)
}
